import { Component, OnInit, ChangeDetectionStrategy, Inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslateModule } from '@ngx-translate/core';
import { AllocationViewAvailableCompartmentDock, AllocationViewAvailableDocksFacade } from '../../../../../store';
import { filter, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ActionButtonComponent, ButtonAction, ButtonSize } from '../../../../action-button';
import {
  AllocationViewAvailableCompartment,
  AllocationViewAvailableDocksModel,
} from '../../../../../store/state/monitoring/allocation-view/models/allocation-view-available-docks.model';
import { alphanumericSortByName, AutoUnsubscribe } from '../../../../../common';
import { DirectivesModule } from '../../../../../common/directives/directives.module';

@Component({
    selector: 'ui-filter-selection-dialog',
    templateUrl: './compartment-docks-filter-selection-dialog.component.html',
    styleUrls: ['./compartment-docks-filter-selection-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ReactiveFormsModule, MatCheckboxModule, TranslateModule, DirectivesModule, ActionButtonComponent]
})
@AutoUnsubscribe
export class CompartmentDocksFilterSelectionDialogComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  buttonAction = ButtonAction;
  buttonSize = ButtonSize;
  formArray: UntypedFormArray;

  constructor(
    public dialogRef: MatDialogRef<CompartmentDocksFilterSelectionDialogComponent>,
    public allocationViewAvailableDocksFacade: AllocationViewAvailableDocksFacade,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      filterName: string;
      label: string;
      filtersFormControlOrGroup: UntypedFormGroup | UntypedFormControl;
    },
    private fb: UntypedFormBuilder,
  ) {
    this.allocationViewAvailableDocksFacade.loadAll();
  }

  ngOnInit(): void {
    this.initListForm();
  }

  ngOnDestroy(): void {
    this.allocationViewAvailableDocksFacade.setInitialState();
  }

  isAllDocksSelected(index: number): boolean {
    return (
      this.getDocks(index).value?.every((dock: AllocationViewAvailableCompartmentDock) => {
        return dock.isSelected;
      }) ?? false
    );
  }

  compartmentToggle(index: number): void {
    if (this.isAnyDockSelected(index)) {
      this.changeCompartmentIsSelectedValue(index, true);
    }
  }

  selectDock(isSelected: boolean, index: number): void {
    if (isSelected || (!isSelected && !this.isAllDocksSelected(index))) {
      this.changeCompartmentIsSelectedValue(index, true);
    }
  }

  initListForm(): void {
    this.subscriptions.push(
      this.allocationViewAvailableDocksFacade.items$
        .pipe(filter(Boolean), take(1))
        .subscribe((availableDocks: AllocationViewAvailableDocksModel) => {
          if (this.data.filtersFormControlOrGroup.value?.compartments?.length > 0) {
            this.setSelectedListForm(availableDocks);
          } else {
            this.setNewListForm(availableDocks);
          }
        }),
    );
  }

  apply(): void {
    const selectedItems: AllocationViewAvailableDocksModel = {
      compartments: [],
    };

    this.formArray.value.forEach(
      (item: { compartment: AllocationViewAvailableCompartment; docks: AllocationViewAvailableCompartmentDock[] }) => {
        const docks = [];

        if (item.compartment.isSelected) {
          item?.docks?.forEach((dockItem: AllocationViewAvailableCompartmentDock) => {
            const { isSelected, ...dock } = dockItem;
            if (isSelected) {
              docks.push(dock);
            }
          });

          selectedItems.compartments.push({
            docks,
            id: item.compartment?.id,
            name: item.compartment?.name,
          });
        }
      },
    );

    this.dialogRef.close({ selectedItems });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  private getDocks(index: number): UntypedFormArray {
    return this.formArray.controls[index]?.get('docks') as UntypedFormArray;
  }

  private changeCompartmentIsSelectedValue(index: number, value: boolean): void {
    this.formArray.controls[index].get('compartment').get('isSelected').setValue(value);
  }

  private isAnyDockSelected(index: number): boolean {
    return this.getDocks(index).value?.some((item: AllocationViewAvailableCompartmentDock) => item.isSelected);
  }

  private setNewListForm(items: AllocationViewAvailableDocksModel): void {
    this.formArray = this.fb.array([]);
    const sortedCompartmentList: AllocationViewAvailableCompartment[] = alphanumericSortByName([...items.compartments]);

    sortedCompartmentList.forEach((compartment: AllocationViewAvailableCompartment, index: number) => {
      this.formArray.push(
        new UntypedFormGroup({
          compartment: new UntypedFormGroup({
            id: new UntypedFormControl(compartment?.id),
            name: new UntypedFormControl(compartment?.name),
            isSelected: new UntypedFormControl(compartment?.isSelected ?? false),
          }),
          docks: new UntypedFormArray([]),
        }),
      );

      this.addDockFormGroup(compartment, index);
    });
  }

  private addDockFormGroup(compartment: AllocationViewAvailableCompartment, i: number): void {
    const sortedDockList: AllocationViewAvailableCompartmentDock[] = alphanumericSortByName([...compartment?.docks]);

    sortedDockList.forEach((dock: AllocationViewAvailableCompartmentDock) => {
      this.getDocks(i).push(
        new UntypedFormGroup({
          id: new UntypedFormControl(dock.id),
          name: new UntypedFormControl(dock.name),
          isSelected: new UntypedFormControl(dock?.isSelected ?? false),
        }),
      );
    });
  }

  private setSelectedListForm(items: AllocationViewAvailableDocksModel): void {
    const selected = items.compartments?.reduce(
      (newArr: AllocationViewAvailableDocksModel, compartment: AllocationViewAvailableCompartment) => {
        this.data?.filtersFormControlOrGroup.value.compartments.forEach(
          (formCompartmentItem: AllocationViewAvailableCompartment) => {
            if (compartment.id === formCompartmentItem.id) {
              const newDockList = [];

              compartment.docks.forEach((dockItem: AllocationViewAvailableCompartmentDock) => {
                const selectedDock = formCompartmentItem.docks.find(
                  (dock: AllocationViewAvailableCompartmentDock) => dock.id === dockItem.id,
                );

                selectedDock
                  ? newDockList.push({ isSelected: true, ...selectedDock })
                  : newDockList.push({ isSelected: false, ...dockItem });
              });

              compartment = {
                isSelected: true,
                ...formCompartmentItem,
                docks: newDockList,
              };
            } else {
              compartment = {
                isSelected: false,
                ...compartment,
              };
            }
          },
        );
        newArr.compartments.push(compartment);
        return newArr;
      },
      { compartments: [] },
    );

    this.setNewListForm(selected);
  }
}
